/*
  PDP buy panel driver.
  ---------------------
  Runs the `_product-form` block and its parts and the `_product-actions` blocks.
  It owns no cart state — `window.themeCart`
  (assets/cart.js) is the only thing that talks to the cart, and this reacts to what
  comes back.

  Fully document-delegated and idempotent, so a theme-editor section re-render needs
  no re-binding and cannot double-bind. The one piece of per-panel work — hiding the
  native select, drawing the first availability pass — is guarded by a flag on the
  element itself.

  Markup contract:
    [data-product-form]        the panel root
    [data-pf-variants]         application/json array of
                               {id, options, available, price, compareAtPriceShort, onSale,
                                discount, tracked, inventory, state, notify, message}
    [data-pf-variant-select]   the native <select name="id"> the form submits; dropped from
                               the tab order once this runs, since it is then invisible
    [data-pf-option="<index>"] an option group; its inputs are radios
    [data-pf-auto-option]      index of an option resolved here rather than rendered
    [data-pf-auto-priority]    its values in preference order, comma separated
    [data-pf-option-value]     the "Color: Toffee" value, rewritten per selection
    [data-pf-option-label]     one value's label, carries `is-unavailable`
    [data-pf-price-prefix]     "From", shown while no size is chosen and the colour's
                               sizes differ in price; hidden once a variant resolves
    [data-pf-colour]           on the colour option group, whose checked value prices
                               the line while no variant resolves
    [data-pf-price-current]    price text
    [data-pf-price-compare]    the compare-at line: currency code, then the struck amount
    [data-pf-price-was]        that amount alone — the code beside it is not struck
    [data-pf-submit]           the add button
    [data-pf-status]           aria-live region for errors and confirmation
    [data-stock-state]         on the root: in-stock | sold-out | coming-soon | unchosen
    [data-stock-notify]        on the root: whether this variant offers the sign-up
    [data-stock-text]          the stock line, per state; falls back to its `data-default`
    [data-product-badges]      the badge row; [data-badge-status] pills toggle per colour
    [data-pf-guide]            the size guide <dialog>
    [data-pf-guide-open]       its trigger
    [data-sg-chart]            a size chart; [data-sg-active-unit] is the unit on show
    [data-sg-unit-btn]         its CM / IN buttons, carrying the unit they select
    [data-sg-chat]             the guide's CTA — announces `chat:open`, and follows its
                               own href only when nothing takes it
    [data-pf-guide-slot]       where the size guide row is moved to, in the size option's
                               head — the design's placement, which the block cannot
                               render into itself

  Variant resolution is Shopify's own rule: a variant matches when every one of its
  option values equals the current selection, position for position. Nothing is
  re-fetched — the panel already holds every variant Shopify rendered.

  The panel opens on a colour and no size — `_product-options` checks nothing else — so
  the first pass resolves no variant and the button reads "Choose a size". The one
  exception is a URL naming a variant (`?variant=`: a link copied after choosing, or the
  quick add's "View product details"), whose size is checked here before that pass;
  Liquid cannot see the query. assets/pdp-render.js carries a chosen size across a
  colour swap by checking the fresh markup itself before announcing it.

  One option may be resolved here instead of shown — the CN/VN sourcing split, set up in
  `_product-options`. Its value is filled in from the priority order before the match runs,
  so everything downstream still sees a complete selection, and resolution subtracts what
  the cart already holds so each value is sold down to zero before the next is offered.

  Four things outside this file are told about a variant change, all through the DOM
  or an event, never by reaching into them:
    • the native <select>, whose `change` the gallery already listens for
    • Shopify's Shop Pay Installments banner, which reads that same select
    • the URL, updated with ?variant= so a copied link opens on the right one
    • `product-form:variant` on `document`, { root, variant, range } — `variant` is null
      when the selection resolves to nothing, and `range` is then the checked colour's
      cheapest variant and whether its sizes differ in price. sections/sticky-atc.liquid
      mirrors the panel from it; the last variant is also parked on `root.__pfVariant`
      for a listener that binds after the first pass.
*/
(function () {
  if (window.__productForm) return;
  window.__productForm = true;

  var RESET_DELAY = 2200;

  /* ---------- Variant resolution ---------- */

  function variantsFor(root) {
    if (root.__pfVariants) return root.__pfVariants;
    var script = root.querySelector('[data-pf-variants]');
    var parsed = [];
    if (script) {
      try {
        parsed = JSON.parse(script.textContent) || [];
      } catch (e) {
        parsed = [];
      }
    }
    root.__pfVariants = parsed;
    return parsed;
  }

  function selection(root) {
    var groups = root.querySelectorAll('[data-pf-option]');
    var values = [];
    for (var i = 0; i < groups.length; i++) {
      var index = parseInt(groups[i].getAttribute('data-pf-option'), 10);
      var checked = groups[i].querySelector('input:checked');
      values[index] = checked ? checked.value : null;
    }
    return values;
  }

  // `loose` wildcards an unanswered option. Resolving *the* variant matches strictly; a
  // reachability probe must not, or a combined listing greys out the whole row.
  function matches(variant, wanted, loose) {
    var options = variant.options || [];
    for (var i = 0; i < wanted.length; i++) {
      if (wanted[i] === null || wanted[i] === undefined) {
        if (loose) continue;
        return false;
      }
      if (options[i] !== wanted[i]) return false;
    }
    return true;
  }

  /* The option `_product-options` withheld from the pickers, and the order to try its
     values in. Null when the block names none, which is the default. */
  function autoConfig(root) {
    if (root.__pfAuto !== undefined) return root.__pfAuto;

    var host = root.querySelector('[data-pf-auto-option]');
    var index = host ? parseInt(host.getAttribute('data-pf-auto-option'), 10) : -1;
    var conf = null;

    if (host && index >= 0) {
      var raw = (host.getAttribute('data-pf-auto-priority') || '').split(',');
      var order = [];
      for (var i = 0; i < raw.length; i++) {
        var value = raw[i].trim().toLowerCase();
        if (value) order.push(value);
      }
      conf = { index: index, order: order };
    }

    root.__pfAuto = conf;
    return conf;
  }

  /* What the cart already holds, per variant. Without this the resolver keeps offering a
     value the customer has already taken every unit of, and every further add is rejected.
     Empty until themeCart has read the cart once — the retry in addToCart covers that. */
  function cartCounts() {
    var state = window.themeCart && window.themeCart.state();
    var items = (state && state.items) || [];
    var counts = {};
    for (var i = 0; i < items.length; i++) {
      counts[items[i].id] = (counts[items[i].id] || 0) + items[i].quantity;
    }
    return counts;
  }

  // Headroom, not just sellability: a variant on "continue selling" reports available at
  // zero, and would hold the queue at the first value for ever.
  function inStock(variant, counts) {
    if (!variant.available) return false;
    if (!variant.tracked) return true;
    return variant.inventory - ((counts && counts[variant.id]) || 0) > 0;
  }

  /* Whether one more can go in the cart — a different question from inStock(), which
     answers "which value still has units" for the resolver's queue. A variant set to keep
     selling at zero has no units to count but can always be bought, so gating the add on
     inStock() would refuse it with an empty cart. */
  function canAdd(variant, counts) {
    if (!variant.available) return false;
    if (variant.oversell || !variant.tracked) return true;
    return variant.inventory - ((counts && counts[variant.id]) || 0) > 0;
  }

  /* Fills the auto option in: first priority value with stock, then any value with stock,
     then anything sellable, then whatever exists so a sold-out size still has a price. */
  function resolveAuto(root, wanted) {
    var conf = autoConfig(root);
    if (!conf) return wanted;

    var variants = variantsFor(root);
    var probe = wanted.slice();
    probe[conf.index] = null;

    var pool = [];
    for (var v = 0; v < variants.length; v++) {
      if (matches(variants[v], probe, true)) pool.push(variants[v]);
    }
    if (!pool.length) return wanted;

    var counts = cartCounts();
    var chosen = null;
    for (var p = 0; p < conf.order.length && !chosen; p++) {
      for (var i = 0; i < pool.length && !chosen; i++) {
        var value = String(pool[i].options[conf.index] || '').toLowerCase();
        if (value === conf.order[p] && inStock(pool[i], counts)) chosen = pool[i];
      }
    }
    for (var s = 0; s < pool.length && !chosen; s++) {
      if (inStock(pool[s], counts)) chosen = pool[s];
    }
    for (var a = 0; a < pool.length && !chosen; a++) {
      if (pool[a].available) chosen = pool[a];
    }

    chosen = chosen || pool[0];
    probe[conf.index] = chosen.options[conf.index];
    return probe;
  }

  /* Whether any value of the withheld option still has a unit spare for this selection —
     a size can be in stock while the customer already holds every one of them. Always true
     when no option is withheld, so an ordinary product is unaffected. */
  function hasHeadroom(root, variant) {
    var conf = autoConfig(root);
    if (!conf || !variant) return true;

    var probe = (variant.options || []).slice();
    probe[conf.index] = null;

    var counts = cartCounts();
    var variants = variantsFor(root);
    for (var v = 0; v < variants.length; v++) {
      if (matches(variants[v], probe, true) && canAdd(variants[v], counts)) return true;
    }
    return false;
  }

  /* The size the URL asks for, checked before the first pass. Only a variant this panel
     holds: after a colour swap the address still names the previous product's for a
     moment, and that must not check anything here. */
  function checkRequested(root) {
    var id = null;
    try {
      id = new URL(window.location.href).searchParams.get('variant');
    } catch (e) {
      return;
    }
    if (!id) return;

    var variants = variantsFor(root);
    var wanted = null;
    for (var i = 0; i < variants.length && !wanted; i++) {
      if (String(variants[i].id) === id) wanted = variants[i].options || [];
    }
    if (!wanted) return;

    var groups = root.querySelectorAll('[data-pf-option]');
    for (var g = 0; g < groups.length; g++) {
      var index = parseInt(groups[g].getAttribute('data-pf-option'), 10);
      var inputs = groups[g].querySelectorAll('[data-pf-option-input]');
      for (var n = 0; n < inputs.length; n++) {
        if (inputs[n].value === wanted[index]) inputs[n].checked = true;
      }
    }
  }

  function matchVariant(root) {
    var variants = variantsFor(root);
    // No option groups at all — a single-variant product, or one whose only option is the
    // withheld one. The resolver still has to run in the second case.
    if (!root.querySelector('[data-pf-option]') && !autoConfig(root)) return variants[0] || null;

    var wanted = resolveAuto(root, selection(root));
    for (var i = 0; i < variants.length; i++) {
      if (matches(variants[i], wanted)) return variants[i];
    }
    return null;
  }

  /* ---------- Availability ----------
     Re-derived per change: the option's own position is wildcarded, never the others. */

  function markAvailability(root) {
    var wanted = selection(root);
    var variants = variantsFor(root);
    var groups = root.querySelectorAll('[data-pf-option]');
    var auto = autoConfig(root);

    for (var g = 0; g < groups.length; g++) {
      var index = parseInt(groups[g].getAttribute('data-pf-option'), 10);
      var labels = groups[g].querySelectorAll('[data-pf-option-label]');

      for (var l = 0; l < labels.length; l++) {
        var value = labels[l].getAttribute('data-pf-option-label');
        var probe = wanted.slice();
        probe[index] = value;
        // A size is reachable if *any* of the auto option's values still has it, so it is
        // struck through only once every one of them is gone.
        if (auto) probe[auto.index] = null;

        var reachable = false;
        for (var v = 0; v < variants.length; v++) {
          if (!variants[v].available) continue;
          if (matches(variants[v], probe, true)) {
            reachable = true;
            break;
          }
        }
        labels[l].classList.toggle('is-unavailable', !reachable);
      }
    }
  }

  /* ---------- Rendering ---------- */

  function setStatus(root, message, tone) {
    var status = root.querySelector('[data-pf-status]');
    if (!status) return;
    status.textContent = message || '';
    status.classList.toggle('is-error', tone === 'error');
    status.classList.toggle('is-success', tone === 'success');
  }

  /* The checked colour's range, for a line with no variant to price yet — the line
     `_product-price` opens on, rebuilt here when another colour is picked with no size.
     Its cheapest variant, with the largest compare-at at that price, and whether its sizes
     span two prices. Null with no colour row or nothing checked in it. */
  function colourRange(root) {
    var group = root.querySelector('[data-pf-option][data-pf-colour]');
    var checked = group && group.querySelector('input:checked');
    if (!checked) return null;

    var index = parseInt(group.getAttribute('data-pf-option'), 10);
    var variants = variantsFor(root);
    var cheapest = null;
    var varies = false;
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      if ((v.options || [])[index] !== checked.value) continue;
      if (!cheapest) cheapest = v;
      else if (v.priceCents < cheapest.priceCents) {
        cheapest = v;
        varies = true;
      } else if (v.priceCents > cheapest.priceCents) varies = true;
      else if (v.compareAtCents > cheapest.compareAtCents) cheapest = v;
      // Same line either way, so the one with stock stands for the colour.
      else if (v.compareAtCents === cheapest.compareAtCents && v.available && !cheapest.available) cheapest = v;
    }
    return cheapest ? { variant: cheapest, varies: varies } : null;
  }

  function renderPrice(root, variant) {
    var box = root.querySelector('[data-pf-price]');
    var current = root.querySelector('[data-pf-price-current]');
    var compare = root.querySelector('[data-pf-price-compare]');
    var was = root.querySelector('[data-pf-price-was]');
    var prefix = root.querySelector('[data-pf-price-prefix]');

    // No size yet: the checked colour's range, "From" when its sizes differ. Nothing to
    // range either leaves the server's line standing.
    var range = variant ? null : colourRange(root);
    var shown = variant || (range && range.variant);
    if (!shown) return;

    if (prefix) prefix.hidden = !(range && range.varies);
    // The compare-at line carries the currency, so the sale figure drops it. Only the
    // amount is rewritten — the code is markup and the strike belongs to it alone.
    if (current) current.textContent = shown.onSale ? shown.priceShort : shown.price;
    if (box) box.classList.toggle('is-on-sale', !!shown.onSale);
    if (was) was.textContent = shown.compareAtPriceShort || '';
    if (compare) compare.hidden = !shown.onSale;
  }

  function renderOptionValues(root) {
    var groups = root.querySelectorAll('[data-pf-option]');
    for (var i = 0; i < groups.length; i++) {
      var checked = groups[i].querySelector('input:checked');
      var readout = groups[i].querySelector('[data-pf-option-value]');
      if (readout) readout.textContent = checked ? checked.value : '';
    }
  }

  // Sold out and low stock are the colour's, not the size's, and the badge row works them
  // out from its own data — this only names the variant. The sale figure is the exception:
  // it has to agree with the price this panel is showing, so it travels with it.
  function renderBadges(root, variant) {
    if (!root) return;

    // No size yet: the checked colour's own pills, sale figure included, as a card asks.
    if (!variant) {
      var group = root.querySelector('[data-pf-colour] input:checked');
      if (!group) return;
      root.dispatchEvent(
        new CustomEvent('product-badges:update', {
          bubbles: true,
          detail: { group: group.value }
        })
      );
      return;
    }

    root.dispatchEvent(
      new CustomEvent('product-badges:update', {
        bubbles: true,
        detail: {
          options: variant.options,
          onSale: variant.onSale,
          discount: variant.discount
        }
      })
    );
  }

  /* The panel's stock state, which the CSS reads to swap the add to cart for the
     notice and to pick which of its two panels shows. The per-variant message is the
     one string that varies inside a state, so it is written here and falls back to
     the copy Liquid rendered from the _product-form block's settings. */
  function renderStock(root, variant) {
    var state = 'unchosen';
    var notify = false;

    if (variant) {
      state = variant.state || (variant.available ? 'in-stock' : 'sold-out');
      notify = !!variant.notify;
    }

    root.setAttribute('data-stock-state', state);
    root.setAttribute('data-stock-notify', notify ? 'true' : 'false');

    var lines = root.querySelectorAll('[data-stock-text]');
    for (var i = 0; i < lines.length; i++) {
      var fallback = lines[i].getAttribute('data-default') || '';
      lines[i].textContent = (variant && variant.message) || fallback;
    }
  }

  function renderSubmit(root, variant) {
    var submit = root.querySelector('[data-pf-submit]');
    if (!submit) return;
    var label = submit.querySelector('.button__label');

    // Stopping here beats letting the add fail: Shopify's cart errors quote the variant
    // title, which would print the withheld option on the page.
    var spent = variant && variant.available && !hasHeadroom(root, variant);
    var unavailable = !variant || !variant.available || spent;
    submit.disabled = unavailable;
    submit.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
    submit.classList.toggle('button--disabled', unavailable);

    if (!label) return;
    if (!variant) label.textContent = submit.getAttribute('data-label-choose') || '';
    else if (variant.state === 'coming-soon') {
      label.textContent =
        submit.getAttribute('data-label-coming-soon') || submit.getAttribute('data-label-sold-out') || '';
    } else if (!variant.available) label.textContent = submit.getAttribute('data-label-sold-out') || '';
    else if (spent) label.textContent = submit.getAttribute('data-label-max') || submit.getAttribute('data-label-sold-out') || '';
    else label.textContent = submit.getAttribute('data-label-add') || '';
  }

  /* Setting `.value` does not fire `change`, so it is dispatched — and only when the id
     actually moved, or the gallery re-filters on every click. */
  function syncSelect(root, variant) {
    var select = root.querySelector('[data-pf-variant-select]');
    if (!select || !variant) return;

    var next = String(variant.id);
    if (select.value === next) return;
    select.value = next;

    // Flag the round trip so the native-control branch below does not re-derive what this
    // just did. `dispatchEvent` is synchronous, so the flag is down immediately.
    root.__pfSyncing = true;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    root.__pfSyncing = false;
  }

  /* Enhanced, the select is not a control: the swatches are, and it stays in the DOM only to
     carry the posted `id`. Clipped to a pixel it is still a tab stop with nothing to see, and
     Enter on a select is implicit submission — so tabbing off the price and pressing Enter
     added to cart from an element the customer could not find. Dropped from the tab order
     here rather than in the markup: with no driver the CSS un-clips it and it is the picker,
     which has to stay reachable. */
  function dropFallbackSelect(root) {
    var select = root.querySelector('[data-pf-variant-select]');
    if (!select) return;

    select.tabIndex = -1;
    select.setAttribute('aria-hidden', 'true');

    // Its label would otherwise read out as loose text beside a control nothing can reach.
    var labels = select.labels || [];
    for (var i = 0; i < labels.length; i++) labels[i].setAttribute('aria-hidden', 'true');
  }

  /* Replace, not push: stacking history entries would trap the back button inside the
     picker. The entry's state is kept — assets/pdp-render.js stamps it to tell its own
     entries from a hash change. */
  function syncUrl(root, variant) {
    if (!variant || !window.history || !window.history.replaceState) return;
    try {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (e) {
      /* A URL the browser will not parse is not worth failing a variant change over. */
    }
  }

  // One place decides everything the panel shows for a selection, so a variant change
  // and a failed add cannot leave two parts of it disagreeing.
  function sync(root, options) {
    var variant = matchVariant(root);
    root.__pfVariant = variant;
    // The checked colour's line while no variant resolves: what the price shows, and what
    // the native select carries so Shop Pay and every other reader of it follow the colour.
    var range = variant ? null : colourRange(root);

    renderOptionValues(root);
    markAvailability(root);
    renderPrice(root, variant);
    renderBadges(root, variant);
    renderSubmit(root, variant);
    renderStock(root, variant);
    syncSelect(root, variant || (range && range.variant));

    // Skipped on the first pass: it resolves a variant only when the URL already named it.
    if (!options || !options.initial) syncUrl(root, variant);

    document.dispatchEvent(
      new CustomEvent('product-form:variant', {
        bubbles: true,
        detail: { root: root, variant: variant, range: range },
      })
    );
  }

  /* ---------- Add to cart ---------- */

  // Back to the truth: a rejected add may mean the variant sold out between the page load
  // and the click.
  function addFailed(root, error) {
    var submit = root.querySelector('[data-pf-submit]');
    if (submit) submit.classList.remove('is-loading');

    // Shopify's cart errors quote the variant title — "…Supernova / XXL / CN" — so the
    // theme's own wording stands in whenever an option is being withheld.
    var fallback = root.getAttribute('data-error-fallback') || '';
    setStatus(root, autoConfig(root) ? fallback : error.message || fallback, 'error');
    sync(root);
  }

  function addToCart(root, form, variant, mayRetry) {
    var submit = root.querySelector('[data-pf-submit]');
    var label = submit && submit.querySelector('.button__label');
    var quantityField = form.querySelector('[data-pf-quantity]');
    var quantity = parseInt(quantityField && quantityField.value, 10) || 1;

    if (submit) {
      submit.disabled = true;
      submit.classList.add('is-loading');
    }
    setStatus(root, '');

    window.themeCart
      .add({ id: variant.id, quantity: quantity }, 'product-form')
      .then(function () {
        if (submit) submit.classList.remove('is-loading');
        if (label) label.textContent = submit.getAttribute('data-label-added') || '';
        setStatus(root, submit ? submit.getAttribute('data-label-added') || '' : '', 'success');

        // The cart drawer is the header's to open, and it opens off `cart:updated`
        // which themeCart has already announced. Nothing to call from here.
        clearTimeout(root.__pfResetTimer);
        root.__pfResetTimer = setTimeout(function () {
          sync(root);
          setStatus(root, '');
        }, RESET_DELAY);
      })
      .catch(function (error) {
        /* A cart populated before this page loaded is invisible to the resolver, so its
           first add can be rejected for a value it thinks has headroom. Re-read the cart,
           re-resolve, and try the next value once — matchVariant touches no DOM, so the
           button stays in its loading state throughout. */
        if (!mayRetry || !autoConfig(root)) return addFailed(root, error);

        window.themeCart
          .refresh('product-form')
          .then(function () {
            var next = matchVariant(root);
            if (next && next.id !== variant.id && next.available) {
              return addToCart(root, form, next, false);
            }
            addFailed(root, error);
          })
          .catch(function () {
            addFailed(root, error);
          });
      });
  }

  function submitForm(event, form) {
    var root = form.closest('[data-product-form]');
    if (!root) return;

    // No cart layer means no enhancement: let the browser post the form itself
    // rather than swallowing the click and adding nothing.
    if (!window.themeCart) return;

    event.preventDefault();

    var variant = root.__pfVariant || matchVariant(root);
    if (!variant) {
      setStatus(root, root.getAttribute('data-error-choose') || '', 'error');
      return;
    }
    if (!variant.available) return;

    addToCart(root, form, variant, true);
  }

  /* The fit bar's tooltip is the theme's shared one — assets/tooltip.js. */

  /* ---------- Size guide ---------- */

  function openGuide(trigger) {
    var root = trigger.closest('[data-product-form]');
    var scope = root ? root.parentElement : document;
    var sheet = scope.querySelector('[data-pf-guide]') || document.querySelector('[data-pf-guide]');
    if (!sheet || typeof sheet.showModal !== 'function') return;

    // Every close path (Esc, backdrop, button) fires `close`, so park the return
    // target on the dialog rather than tracking each exit separately.
    sheet.__pfReturnFocus = trigger;
    sheet.showModal();
    var close = sheet.querySelector('[data-pf-guide-close]');
    if (close) close.focus();
  }

  /* The whole row moves, dialog included, or `_size-guide` leaves an empty wrapper taking
     the panel's gap. No slot and it stays where it rendered. */
  function placeGuide(root) {
    var slot = root.querySelector('[data-pf-guide-slot]');
    var trigger = root.querySelector('[data-pf-guide-open]');
    if (!slot || !trigger) return;

    var row = trigger.closest('.product-form__guide-row') || trigger;
    if (!slot.contains(row)) slot.appendChild(row);
  }

  /* Both units are already in the cells and CSS shows one, so this only moves the flag
     — no re-measuring, and the table still reads if this never runs. */
  function setChartUnit(btn) {
    var chart = btn.closest('[data-sg-chart]');
    if (!chart) return;

    var unit = btn.getAttribute('data-sg-unit-btn');
    chart.setAttribute('data-sg-active-unit', unit);

    var buttons = chart.querySelectorAll('[data-sg-unit-btn]');
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].setAttribute('aria-pressed', buttons[i] === btn ? 'true' : 'false');
    }
  }
  /* ---------- Wiring ---------- */

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-product-form]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].__pfReady) continue;
      roots[i].__pfReady = true;
      // The block's inline script sets this on first paint, but an inline script in
      // section-rendered markup never runs — so the driver owns it too.
      roots[i].classList.add('product-form--enhanced');
      dropFallbackSelect(roots[i]);
      placeGuide(roots[i]);
      checkRequested(roots[i]);
      sync(roots[i], { initial: true });
    }
  }

  document.addEventListener('change', function (e) {
    var target = e.target;
    if (!target || !target.closest) return;

    if (target.closest('[data-pf-option]')) {
      var root = target.closest('[data-product-form]');
      if (!root) return;
      setStatus(root, '');
      sync(root);
      return;
    }

    // The native select is the fallback path, and the source of truth either way: a
    // change made there has to push back into the pickers.
    if (target.matches('[data-pf-variant-select]')) {
      var owner = target.closest('[data-product-form]');
      if (!owner || owner.__pfSyncing) return;

      var variants = variantsFor(owner);
      var chosen = null;
      for (var v = 0; v < variants.length; v++) {
        if (String(variants[v].id) === target.value) {
          chosen = variants[v];
          break;
        }
      }
      if (!chosen) return;

      // The auto option has no group to push back into, and `sync` re-resolves it — so
      // picking a specific one here does not stick past the next change.
      var groups = owner.querySelectorAll('[data-pf-option]');
      for (var g = 0; g < groups.length; g++) {
        var index = parseInt(groups[g].getAttribute('data-pf-option'), 10);
        var input = groups[g].querySelector('input[value="' + CSS.escape(chosen.options[index] || '') + '"]');
        if (input) input.checked = true;
      }

      sync(owner);
      return;
    }

    if (target.matches('[data-pa-compare]')) {
      var fired = document.dispatchEvent(
        new CustomEvent('compare:toggle', {
          bubbles: true,
          cancelable: true,
          detail: {
            handle: target.getAttribute('data-product-handle'),
            productId: target.getAttribute('data-product-id'),
            variantId: target.getAttribute('data-variant-id'),
            active: target.checked,
            source: 'product-form',
          },
        })
      );
      // A listener that took ownership resets the box and drives it itself.
      if (!fired) target.checked = !target.checked;
    }
  });

  /* Enter in a form submits it, and this form's submit is the add to cart — so Enter on a
     swatch radio bought the current variant instead of choosing the value under the cursor.
     The chips read as buttons, so Enter picks the value and the `change` above does the rest.
     Space already selects natively and is left alone. */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;

    var target = e.target;
    if (!target || !target.closest) return;

    var input = target.closest('[data-pf-option-input]');
    if (!input) return;

    e.preventDefault();
    if (input.checked) return;

    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!target || !target.closest) return;

    var opener = target.closest('[data-pf-guide-open]');
    if (opener) {
      openGuide(opener);
      return;
    }

    var unitBtn = target.closest('[data-sg-unit-btn]');
    if (unitBtn) {
      setChartUnit(unitBtn);
      return;
    }

    /* Chat is an app embed, so the theme only announces the intent. Untaken, the click is
       left alone and the CTA follows its own href — the chart's contact link, which is the
       fallback whenever chat cannot answer. */
    var chat = target.closest('[data-sg-chat]');
    if (chat) {
      var taken = !document.dispatchEvent(
        new CustomEvent('chat:open', {
          bubbles: true,
          cancelable: true,
          detail: { source: 'size-guide' },
        })
      );
      if (taken) {
        e.preventDefault();
        // The sheet is in the top layer and would cover the widget.
        var sheet = chat.closest('[data-pf-guide]');
        if (sheet) sheet.close();
      }
      return;
    }

    var closer = target.closest('[data-pf-guide-close]');
    if (closer) {
      var owner = closer.closest('[data-pf-guide]');
      if (owner) owner.close();
      return;
    }

    // A backdrop click targets the dialog element itself.
    if (target.matches('[data-pf-guide]')) {
      target.close();
      return;
    }

    var wishlist = target.closest('[data-pa-wishlist]');
    if (wishlist) {
      var next = wishlist.getAttribute('aria-pressed') !== 'true';
      // Fired from the control and bubbled, so a listener can find the button it belongs
      // to — `assets/wishlist.js` cancels it and drives the state itself.
      var live = wishlist.dispatchEvent(
        new CustomEvent('wishlist:toggle', {
          bubbles: true,
          cancelable: true,
          detail: {
            handle: wishlist.getAttribute('data-product-handle'),
            productId: wishlist.getAttribute('data-product-id'),
            variantId: wishlist.getAttribute('data-variant-id'),
            active: next,
            source: 'product-form',
          },
        })
      );
      if (live) {
        wishlist.setAttribute('aria-pressed', next ? 'true' : 'false');
        var label = wishlist.querySelector('[data-pa-label]');
        var saved = wishlist.getAttribute('data-label-saved');
        var save = wishlist.getAttribute('data-label-save');
        // Write into the typography span, not its wrapper — textContent would drop it.
        if (label && saved && save) {
          (label.firstElementChild || label).textContent = next ? saved : save;
        }
      }
      return;
    }
  });

  /* A write from anywhere else — the upsell sheet, the cart drawer — can take the last
     unit of the size this panel is offering, and headroom is counted from cart state. Its
     own adds are skipped: those confirm on the button and re-sync on their own timer, and
     syncing here would clear the confirmation before it had been read. */
  document.addEventListener('cart:updated', function (e) {
    if (e.detail && e.detail.source === 'product-form') return;

    var roots = document.querySelectorAll('[data-product-form]');
    for (var i = 0; i < roots.length; i++) sync(roots[i]);
  });

  /* The add to cart form, not merely a form inside the panel: the back-in-stock sign-up is
     a real form of its own under the same root, and treating it as an add would swallow
     the signup and put the current variant in the cart instead. `[data-pf-quantity]` is
     rendered by the block directly inside `{% form 'product' %}`, so it marks the right
     form wherever the merchant has placed the add button. */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.closest || !form.querySelector) return;
    if (!form.closest('[data-product-form]')) return;
    if (!form.querySelector('[data-pf-quantity]')) return;
    submitForm(e, form);
  });

  // `close` does not bubble — listen in the capture phase.
  document.addEventListener(
    'close',
    function (e) {
      var sheet = e.target;
      if (!sheet || !sheet.matches || !sheet.matches('[data-pf-guide]')) return;
      var target = sheet.__pfReturnFocus;
      sheet.__pfReturnFocus = null;
      if (target && document.contains(target)) target.focus();
    },
    true
  );

  /* ---------- Lifecycle ---------- */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var roots = e.target.querySelectorAll('[data-product-form]');
    for (var i = 0; i < roots.length; i++) clearTimeout(roots[i].__pfResetTimer);

    var sheets = e.target.querySelectorAll('[data-pf-guide]');
    for (var s = 0; s < sheets.length; s++) {
      sheets[s].__pfReturnFocus = null;
      if (sheets[s].open) sheets[s].close();
    }
  });
})();
